// This file is auto-generated by @hey-api/openapi-ts

import type { Client, Options as Options2, TDataShape } from "./client";
import { client } from "./client.gen";
import type {
  AdminActivityActionsData,
  AdminActivityActionsResponses,
  AdminActivityAnalyticsData,
  AdminActivityAnalyticsErrors,
  AdminActivityAnalyticsResponses,
  AdminDeleteUserData,
  AdminDeleteUserErrors,
  AdminDeleteUserResponses,
  AdminGetUserData,
  AdminGetUserErrors,
  AdminGetUserResponses,
  AdminInviteUserData,
  AdminInviteUserErrors,
  AdminInviteUserResponses,
  AdminListActivityData,
  AdminListActivityErrors,
  AdminListActivityResponses,
  AdminListUsersData,
  AdminListUsersResponses,
  AdminUpdateUserData,
  AdminUpdateUserErrors,
  AdminUpdateUserResponses,
  ChangePasswordData,
  ChangePasswordErrors,
  ChangePasswordResponses,
  CreateConversationData,
  CreateConversationErrors,
  CreateConversationResponses,
  CreateDashboardData,
  CreateDashboardErrors,
  CreateDashboardResponses,
  CreatePinData,
  CreatePinErrors,
  CreatePinResponses,
  DeleteConversationData,
  DeleteConversationErrors,
  DeleteConversationResponses,
  DeleteDashboardData,
  DeleteDashboardErrors,
  DeleteDashboardResponses,
  DeletePinData,
  DeletePinErrors,
  DeletePinResponses,
  GetActivityData,
  GetActivityResponses,
  GetAuthConfigData,
  GetAuthConfigResponses,
  GetConversationData,
  GetConversationErrors,
  GetConversationResponses,
  GetDataSnapshotData,
  GetDataSnapshotErrors,
  GetDataSnapshotResponses,
  GetHealthData,
  GetHealthResponses,
  GetMeData,
  GetMeDetailData,
  GetMeDetailResponses,
  GetMeResponses,
  GetMyVoteData,
  GetMyVoteResponses,
  GetVoteSummaryData,
  GetVoteSummaryResponses,
  GetVoteThemesData,
  GetVoteThemesResponses,
  ListConversationsData,
  ListConversationsResponses,
  ListDashboardsData,
  ListDashboardsResponses,
  ListPinsData,
  ListPinsErrors,
  ListPinsResponses,
  LoginData,
  LoginErrors,
  LoginResponses,
  LogoutData,
  LogoutResponses,
  ResetVotesData,
  ResetVotesResponses,
  SendAgentMessageData,
  SendAgentMessageErrors,
  SendAgentMessageResponses,
  SendCapacityAgentMessageData,
  SendCapacityAgentMessageErrors,
  SendCapacityAgentMessageResponses,
  SignupData,
  SignupErrors,
  SignupResponses,
  SubmitVoteData,
  SubmitVoteErrors,
  SubmitVoteResponses,
  UpdateDashboardData,
  UpdateDashboardErrors,
  UpdateDashboardResponses,
  UpdatePinLayoutData,
  UpdatePinLayoutErrors,
  UpdatePinLayoutResponses,
  UpdateProfileData,
  UpdateProfileErrors,
  UpdateProfileResponses,
} from "./types.gen";

export type Options<
  TData extends TDataShape = TDataShape,
  ThrowOnError extends boolean = boolean,
  TResponse = unknown,
> = Options2<TData, ThrowOnError, TResponse> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client;
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: Record<string, unknown>;
};

/**
 * List Conversations
 *
 * List the current user's conversations, most recently updated first.
 */
export const listConversations = <ThrowOnError extends boolean = false>(
  options?: Options<ListConversationsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<ListConversationsResponses, unknown, ThrowOnError>({
    url: "/agent/conversations",
    ...options,
  });

/**
 * Create Conversation
 *
 * Create a conversation; the title is derived from the first message when omitted.
 */
export const createConversation = <ThrowOnError extends boolean = false>(
  options: Options<CreateConversationData, ThrowOnError>,
) =>
  (options.client ?? client).post<
    CreateConversationResponses,
    CreateConversationErrors,
    ThrowOnError
  >({
    url: "/agent/conversations",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Delete Conversation
 *
 * Delete a conversation and its messages/views (pins on its views go with it).
 */
export const deleteConversation = <ThrowOnError extends boolean = false>(
  options: Options<DeleteConversationData, ThrowOnError>,
) =>
  (options.client ?? client).delete<
    DeleteConversationResponses,
    DeleteConversationErrors,
    ThrowOnError
  >({ url: "/agent/conversations/{conversation_id}", ...options });

/**
 * Get Conversation
 *
 * Return a conversation's messages (in order) and every view it produced.
 */
export const getConversation = <ThrowOnError extends boolean = false>(
  options: Options<GetConversationData, ThrowOnError>,
) =>
  (options.client ?? client).get<GetConversationResponses, GetConversationErrors, ThrowOnError>({
    url: "/agent/conversations/{conversation_id}",
    ...options,
  });

/**
 * Send Agent Message
 *
 * Send a user turn and stream the agent's reply (text deltas, tool calls, views, done).
 */
export const sendAgentMessage = <ThrowOnError extends boolean = false>(
  options: Options<SendAgentMessageData, ThrowOnError>,
) =>
  (options.client ?? client).post<SendAgentMessageResponses, SendAgentMessageErrors, ThrowOnError>({
    url: "/agent/conversations/{conversation_id}/messages",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Send Capacity Agent Message
 *
 * Send a turn to the capacity-scenario agent and stream its reply + dashboard updates.
 *
 * Unlike `sendAgentMessage`, there is no conversation to persist against: the dashboard
 * owns its own scenario state and resends it (`body.scenario`) on every turn, and the
 * client history (`body.history`) is echoed back rather than read from storage.
 */
export const sendCapacityAgentMessage = <ThrowOnError extends boolean = false>(
  options: Options<SendCapacityAgentMessageData, ThrowOnError>,
) =>
  (options.client ?? client).post<
    SendCapacityAgentMessageResponses,
    SendCapacityAgentMessageErrors,
    ThrowOnError
  >({
    url: "/agent/capacity/messages",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * List Dashboards
 *
 * List the user's boards in tab order, creating the default one on first call.
 *
 * Never returns an empty list: the Dashboard page always has a tab to render and
 * pinning always has a target, without the client having to bootstrap either.
 */
export const listDashboards = <ThrowOnError extends boolean = false>(
  options?: Options<ListDashboardsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<ListDashboardsResponses, unknown, ThrowOnError>({
    url: "/agent/dashboards",
    ...options,
  });

/**
 * Create Dashboard
 *
 * Add a board at the end of the tab order.
 */
export const createDashboard = <ThrowOnError extends boolean = false>(
  options: Options<CreateDashboardData, ThrowOnError>,
) =>
  (options.client ?? client).post<CreateDashboardResponses, CreateDashboardErrors, ThrowOnError>({
    url: "/agent/dashboards",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Delete Dashboard
 *
 * Delete a board and the pins on it. Refuses (409) to delete the user's last board.
 */
export const deleteDashboard = <ThrowOnError extends boolean = false>(
  options: Options<DeleteDashboardData, ThrowOnError>,
) =>
  (options.client ?? client).delete<DeleteDashboardResponses, DeleteDashboardErrors, ThrowOnError>({
    url: "/agent/dashboards/{dashboard_id}",
    ...options,
  });

/**
 * Update Dashboard
 *
 * Rename a board or move it in the tab order.
 */
export const updateDashboard = <ThrowOnError extends boolean = false>(
  options: Options<UpdateDashboardData, ThrowOnError>,
) =>
  (options.client ?? client).patch<UpdateDashboardResponses, UpdateDashboardErrors, ThrowOnError>({
    url: "/agent/dashboards/{dashboard_id}",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * List Pins
 *
 * List the user's pinned views in display order, optionally for one board only.
 *
 * Scoped to `dashboard_id` when given (404 if it isn't the caller's board), sorted by that
 * board's own `position`. Unscoped, returns every pin across every board, ordered by
 * (board position, pin position) so the combined order is stable and matches the tab order.
 */
export const listPins = <ThrowOnError extends boolean = false>(
  options?: Options<ListPinsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<ListPinsResponses, ListPinsErrors, ThrowOnError>({
    url: "/agent/pins",
    ...options,
  });

/**
 * Create Pin
 *
 * Pin a view to a board, appended at the end of the order and below every existing card
 * on that board (so a new pin never lands on top of one).
 *
 * `body.dashboard_id` omitted defaults to the caller's first board (created if they somehow
 * have none yet). A view may be pinned to several boards at once — the 409 "already pinned"
 * check is scoped to the target board, not global.
 */
export const createPin = <ThrowOnError extends boolean = false>(
  options: Options<CreatePinData, ThrowOnError>,
) =>
  (options.client ?? client).post<CreatePinResponses, CreatePinErrors, ThrowOnError>({
    url: "/agent/pins",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Update Pin Layout
 *
 * Persist the Dashboard board's arrangement after a drag or resize.
 *
 * Takes the whole board (see `UpdatePinLayoutRequest`); ignores ids the caller does not
 * own rather than failing the batch, so one stale card can't block saving the rest.
 */
export const updatePinLayout = <ThrowOnError extends boolean = false>(
  options: Options<UpdatePinLayoutData, ThrowOnError>,
) =>
  (options.client ?? client).post<UpdatePinLayoutResponses, UpdatePinLayoutErrors, ThrowOnError>({
    url: "/agent/pins/layout",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Delete Pin
 *
 * Unpin a view from the Dashboard.
 */
export const deletePin = <ThrowOnError extends boolean = false>(
  options: Options<DeletePinData, ThrowOnError>,
) =>
  (options.client ?? client).delete<DeletePinResponses, DeletePinErrors, ThrowOnError>({
    url: "/agent/pins/{pin_id}",
    ...options,
  });

/**
 * Reset Votes
 *
 * Delete every vote and clear the clustering state — a between-demo reset.
 *
 * Protected: only an authenticated presenter can wipe the ballot. PostgREST
 * refuses an unfiltered delete, so this filters on `voter_id is not null` —
 * true for every row, since `voter_id` is the primary key. A "not equal to a
 * magic id" filter would be forgeable: the cookie is httponly (unreadable by
 * JS) but a client can still *send* any value it likes, so a voter whose id was
 * the magic value would survive every reset and poison later tallies.
 */
export const resetVotes = <ThrowOnError extends boolean = false>(
  options?: Options<ResetVotesData, ThrowOnError>,
) =>
  (options?.client ?? client).delete<ResetVotesResponses, unknown, ThrowOnError>({
    url: "/votes",
    ...options,
  });

/**
 * Submit Vote
 *
 * Record (or update) the caller's vote, keyed on the `boi-vote-id` cookie.
 *
 * A first-time voter has no valid cookie, so one is generated and set on the
 * response; a malformed cookie is treated as first-time (never fed to the uuid
 * primary key, which would 500). Re-voting from the same browser reuses the
 * cookie and upserts the existing row (`on_conflict="voter_id"`) rather than
 * adding a duplicate. Once the voter cap is reached, only existing voters may
 * keep updating — a new voter is rejected with 429.
 */
export const submitVote = <ThrowOnError extends boolean = false>(
  options: Options<SubmitVoteData, ThrowOnError>,
) =>
  (options.client ?? client).post<SubmitVoteResponses, SubmitVoteErrors, ThrowOnError>({
    url: "/votes",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Get My Vote
 *
 * Return the caller's own vote, resolved from their `boi-vote-id` cookie.
 *
 * No cookie, a malformed cookie, or no stored row → `voted=false` with a null
 * vote; otherwise the stored score and comments come back so the /vote page can
 * prefill an edit.
 */
export const getMyVote = <ThrowOnError extends boolean = false>(
  options?: Options<GetMyVoteData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetMyVoteResponses, unknown, ThrowOnError>({
    url: "/votes/me",
    ...options,
  });

/**
 * Get Vote Summary
 *
 * Live vote stats for the presenter: total, average, and a 1-5 distribution.
 *
 * Cheap enough for the frontend to poll every few seconds — one table read and
 * a Python aggregation, no LLM. `distribution` is always five zero-filled
 * buckets (scores 1..5); `average` is null when no votes exist.
 */
export const getVoteSummary = <ThrowOnError extends boolean = false>(
  options?: Options<GetVoteSummaryData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetVoteSummaryResponses, unknown, ThrowOnError>({
    url: "/votes/summary",
    ...options,
  });

/**
 * Get Vote Themes
 *
 * Cluster audience pros and cons into labeled themes for the presenter.
 *
 * The presenter auto-polls this while votes stream in. Pros and cons are
 * clustered concurrently and independently, each biggest-theme-first. Existing
 * themes are frozen across polls and only *appended* to (a full rebuild happens
 * only when a re-vote removes a previously-clustered comment), so labels and
 * groupings stay stable. Counts and verbatim examples are always derived
 * deterministically — the model never authors a count or a quote — and a
 * clustering failure degrades rather than 500s.
 */
export const getVoteThemes = <ThrowOnError extends boolean = false>(
  options?: Options<GetVoteThemesData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetVoteThemesResponses, unknown, ThrowOnError>({
    url: "/votes/themes",
    ...options,
  });

/**
 * Health
 *
 * Return a simple health status payload.
 */
export const getHealth = <ThrowOnError extends boolean = false>(
  options?: Options<GetHealthData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetHealthResponses, unknown, ThrowOnError>({
    url: "/health",
    ...options,
  });

/**
 * Auth Config
 *
 * Which sign-in flow this deployment uses — SSO redirect or password form.
 *
 * Unauthenticated by design: the SPA has to call it before anyone is signed in
 * to know which login screen to render.
 */
export const getAuthConfig = <ThrowOnError extends boolean = false>(
  options?: Options<GetAuthConfigData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetAuthConfigResponses, unknown, ThrowOnError>({
    url: "/auth/config",
    ...options,
  });

/**
 * Auth Signup
 *
 * Register a new user with email verification.
 */
export const signup = <ThrowOnError extends boolean = false>(
  options: Options<SignupData, ThrowOnError>,
) =>
  (options.client ?? client).post<SignupResponses, SignupErrors, ThrowOnError>({
    url: "/auth/signup",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Login
 *
 * Authenticate via Supabase, then set the signed session cookie.
 *
 * The Supabase access/refresh tokens never leave the backend — the browser
 * only ever sees the HMAC cookie this endpoint sets.
 */
export const login = <ThrowOnError extends boolean = false>(
  options: Options<LoginData, ThrowOnError>,
) =>
  (options.client ?? client).post<LoginResponses, LoginErrors, ThrowOnError>({
    url: "/auth/login",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Logout
 *
 * Sign out — clear the session cookie.
 */
export const logout = <ThrowOnError extends boolean = false>(
  options?: Options<LogoutData, ThrowOnError>,
) =>
  (options?.client ?? client).post<LogoutResponses, unknown, ThrowOnError>({
    url: "/auth/logout",
    ...options,
  });

/**
 * Auth Me
 *
 * Get the current user's profile.
 */
export const getMe = <ThrowOnError extends boolean = false>(
  options?: Options<GetMeData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetMeResponses, unknown, ThrowOnError>({
    url: "/auth/me",
    ...options,
  });

/**
 * Auth Me Detail
 *
 * Get the current user's profile with timeline fields.
 */
export const getMeDetail = <ThrowOnError extends boolean = false>(
  options?: Options<GetMeDetailData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetMeDetailResponses, unknown, ThrowOnError>({
    url: "/auth/me/detail",
    ...options,
  });

/**
 * Auth Update Profile
 *
 * Update current user's name.
 */
export const updateProfile = <ThrowOnError extends boolean = false>(
  options: Options<UpdateProfileData, ThrowOnError>,
) =>
  (options.client ?? client).put<UpdateProfileResponses, UpdateProfileErrors, ThrowOnError>({
    url: "/auth/profile",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Change Password
 *
 * Change the current user's password (verifies current password first).
 */
export const changePassword = <ThrowOnError extends boolean = false>(
  options: Options<ChangePasswordData, ThrowOnError>,
) =>
  (options.client ?? client).put<ChangePasswordResponses, ChangePasswordErrors, ThrowOnError>({
    url: "/auth/password",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Activity
 *
 * Get the current user's activity log.
 */
export const getActivity = <ThrowOnError extends boolean = false>(
  options?: Options<GetActivityData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetActivityResponses, unknown, ThrowOnError>({
    url: "/auth/activity",
    ...options,
  });

/**
 * Admin List Users
 *
 * List all users (admin only).
 */
export const adminListUsers = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListUsersData, ThrowOnError>,
) =>
  (options?.client ?? client).get<AdminListUsersResponses, unknown, ThrowOnError>({
    url: "/admin/users",
    ...options,
  });

/**
 * Admin Delete User
 *
 * Delete a user (admin only). Self-deletion blocked.
 */
export const adminDeleteUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminDeleteUserData, ThrowOnError>,
) =>
  (options.client ?? client).delete<AdminDeleteUserResponses, AdminDeleteUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
  });

/**
 * Admin Get User
 *
 * Get a single user detail with activity logs (admin only).
 */
export const adminGetUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminGetUserData, ThrowOnError>,
) =>
  (options.client ?? client).get<AdminGetUserResponses, AdminGetUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
  });

/**
 * Admin Update User
 *
 * Update a user's name or role (admin only). Self-demotion blocked.
 */
export const adminUpdateUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminUpdateUserData, ThrowOnError>,
) =>
  (options.client ?? client).put<AdminUpdateUserResponses, AdminUpdateUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin Invite User
 *
 * Invite a user by email (admin only).
 */
export const adminInviteUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminInviteUserData, ThrowOnError>,
) =>
  (options.client ?? client).post<AdminInviteUserResponses, AdminInviteUserErrors, ThrowOnError>({
    url: "/admin/invite",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin List Activity
 *
 * List activity across all users (admin only).
 *
 * Search, filtering, sorting, and pagination are all applied server-side by
 * the list_recent_activity RPC — the client sends params and renders results.
 */
export const adminListActivity = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListActivityData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    AdminListActivityResponses,
    AdminListActivityErrors,
    ThrowOnError
  >({ url: "/admin/activity", ...options });

/**
 * Admin Activity Analytics
 *
 * Aggregated activity analytics for the admin dashboard (admin only).
 */
export const adminActivityAnalytics = <ThrowOnError extends boolean = false>(
  options?: Options<AdminActivityAnalyticsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    AdminActivityAnalyticsResponses,
    AdminActivityAnalyticsErrors,
    ThrowOnError
  >({ url: "/admin/activity/analytics", ...options });

/**
 * Admin Activity Actions
 *
 * Distinct activity actions across all time (admin only).
 *
 * Used to populate the activity filter dropdown so it matches the full,
 * unbounded table scope rather than the windowed analytics breakdown.
 */
export const adminActivityActions = <ThrowOnError extends boolean = false>(
  options?: Options<AdminActivityActionsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<AdminActivityActionsResponses, unknown, ThrowOnError>({
    url: "/admin/activity/actions",
    ...options,
  });

/**
 * Data Snapshot
 *
 * Serve the pre-built site snapshot as opaque, pre-gzipped bytes.
 *
 * Deliberately shape-agnostic: the payload's schema is owned by the frontend's
 * hand-written types, so nothing here parses, validates or reshapes it and
 * replacing the file with a real query layer needs no change to this route.
 * 404 is a normal state, not an error — the SPA reads it as "no real data
 * available, use the demo dataset".
 */
export const getDataSnapshot = <ThrowOnError extends boolean = false>(
  options?: Options<GetDataSnapshotData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetDataSnapshotResponses, GetDataSnapshotErrors, ThrowOnError>({
    url: "/data/snapshot",
    ...options,
  });
